import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Copy, Eye, EyeOff, ExternalLink, Pencil, ShieldOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import VaultItemDialog from "@/components/VaultItemDialog";

export default function SharedWithMe() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});
  const [editing, setEditing] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shared/with-me");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyValue = async (val, label) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Unable to copy");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.08em] text-[color:var(--vault-text-tertiary)]">Shared with you</p>
        <h1 className="font-[Outfit] text-4xl tracking-tight font-semibold mt-1">Other people&apos;s vault items</h1>
        <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-xl">
          Items that family or trusted contacts have explicitly shared with you.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[color:var(--vault-text-tertiary)]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="vault-card p-12 text-center" data-testid="shared-empty-state">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center text-white" style={{ background: "var(--vault-secondary)" }}>
            <Inbox size={24} />
          </div>
          <h3 className="font-[Outfit] text-xl mt-4 font-medium">Nothing shared yet</h3>
          <p className="text-[color:var(--vault-text-secondary)] mt-2 max-w-md mx-auto">
            When someone shares a vault item with you, it&apos;ll appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shared-grid">
          {items.map((it) => (
            <div key={it.id} className="vault-card vault-card-hover p-5" data-testid={`shared-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-[Outfit] text-lg font-medium truncate">{it.title}</h3>
                  <div className="text-xs text-[color:var(--vault-text-tertiary)] mt-0.5">
                    From {it.shared_by_email}
                  </div>
                  {it.username && (
                    <div className="text-sm text-[color:var(--vault-text-secondary)] truncate mt-1">{it.username}</div>
                  )}
                  {it.url && (
                    <a
                      href={it.url.startsWith("http") ? it.url : `https://${it.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[color:var(--vault-info)] hover:underline mt-1"
                    >
                      <ExternalLink size={12} /> <span className="truncate max-w-[180px]">{it.url}</span>
                    </a>
                  )}
                </div>
                <span className="pill shrink-0 capitalize">
                  {it.shared_permission === "edit" ? <Pencil size={11} /> : <ShieldOff size={11} />}
                  {it.shared_permission}
                </span>
              </div>

              <div className="mt-4 rounded-xl bg-[#F1F5FA] border border-[color:var(--vault-border)] px-3 py-2 flex items-center justify-between gap-2">
                <div className="font-mono text-sm tracking-wider truncate text-[color:var(--vault-text-primary)]">
                  {revealed[it.id] ? (it.password || "—") : (it.password ? "•".repeat(12) : "—")}
                </div>
                <div className="flex shrink-0">
                  <button
                    onClick={() => setRevealed((r) => ({ ...r, [it.id]: !r[it.id] }))}
                    className="p-2 rounded-lg text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
                    title="Reveal"
                    data-testid={`shared-reveal-${it.id}`}
                  >
                    {revealed[it.id] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button
                    onClick={() => copyValue(it.password, "Password")}
                    className="p-2 rounded-lg text-[color:var(--vault-text-secondary)] hover:text-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-light)]"
                    title="Copy"
                    data-testid={`shared-copy-${it.id}`}
                  >
                    <Copy size={15} />
                  </button>
                </div>
              </div>

              {it.shared_permission === "edit" && (
                <button
                  onClick={() => { setEditing(it); setDlgOpen(true); }}
                  className="mt-3 text-xs text-[color:var(--vault-brand)] hover:underline"
                  data-testid={`shared-edit-${it.id}`}
                >
                  Edit this item
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <VaultItemDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        item={editing}
        onSaved={load}
      />
    </div>
  );
}
